import { useState, useEffect, useCallback } from "react"
import { Modal, ModalBody, ModalHeader, Row, Col, Label, Input, Button, Form } from "reactstrap"
import { useFormik } from "formik"

import ButtonTooltip from "components/Common/ButtonTooltip"
import { useToastMessage } from "hooks/useToastMessage"
import { useUploadImages } from "hooks/imageHook"
import { useEditVehicleStock } from "hooks/stockHook"
import { useImageViewer } from "hooks/useImageViewer"
import ImageViewer from "components/Common/ImageViewer/ImageViewer"

const SLOTS = [
  { type: "chassis_image", label: "Full Chassis Image" },
  { type: "engine_image", label: "Full Engine Image" },
  { type: "tire_image", label: "Tire Size Image" },
]

// Single image slot: capture/replace one confirmation photo.
const ImageSlotCard = ({ label, value, type, originalFile, onFileChange, onDelete, onRestore, openImageViewer }) => {
  const hasExistingFile = originalFile?.id
  const hasNewFile = value instanceof File
  const isDeleted = !value && hasExistingFile
  const previewUrl = value?.url || (value instanceof File ? URL.createObjectURL(value) : null)

  return (
    <Col md={4} className="mb-4">
      <div className="border rounded-3 bg-white p-3 h-100">
        <div className="d-flex align-items-center justify-content-between mb-2">
          <Label className="fw-semibold mb-0">{label}</Label>
          {hasNewFile && (
            <span className="badge bg-warning bg-opacity-10 text-warning rounded-pill">
              <i className="bx bx-sync me-1"></i>New
            </span>
          )}
          {isDeleted && (
            <span className="badge bg-danger bg-opacity-10 text-danger rounded-pill">
              <i className="bx bx-trash me-1"></i>Removed
            </span>
          )}
        </div>

        <Input
          type="file"
          accept="image/*"
          onChange={e => onFileChange(e, type)}
          className="d-none"
          id={`confirm-file-${type}`}
        />

        {previewUrl ? (
          <div className="position-relative">
            <img
              src={previewUrl}
              alt={label}
              className="w-100 rounded-2"
              style={{ height: 140, objectFit: "cover" }}
            />
            <div className="d-flex gap-2 justify-content-center mt-2">
              {value?.url && (
                <Button color="light" size="sm" onClick={() => openImageViewer(value.url, label)}>
                  <i className="bx bx-show"></i>
                </Button>
              )}
              <Button color="light" size="sm" onClick={() => document.getElementById(`confirm-file-${type}`).click()}>
                <i className="bx bx-upload"></i>
              </Button>
              <Button color="light" size="sm" onClick={() => onDelete(type)}>
                <i className="bx bx-trash"></i>
              </Button>
            </div>
          </div>
        ) : isDeleted ? (
          <div className="bg-danger bg-opacity-10 rounded-3 p-3 text-center">
            <p className="text-danger small mb-2">Will be removed on save</p>
            <Button color="light" size="sm" onClick={() => onRestore(type)}>
              <i className="bx bx-undo me-1"></i>Undo
            </Button>
          </div>
        ) : (
          <div
            className="bg-light rounded-3 p-4 text-center"
            style={{ cursor: "pointer" }}
            onClick={() => document.getElementById(`confirm-file-${type}`).click()}
          >
            <i className="bx bx-cloud-upload fs-2 text-primary"></i>
            <p className="fw-medium small mb-0 mt-2">Upload {label}</p>
          </div>
        )}
      </div>
    </Col>
  )
}

export default function OnboardingConfirmationUpdate({ stock = null }) {
  const [modal, setModal] = useState(false)
  const [slots, setSlots] = useState({ chassis_image: null, engine_image: null, tire_image: null })
  const [originalFiles, setOriginalFiles] = useState({ chassis_image: null, engine_image: null, tire_image: null })

  const { viewerOpen, selectedImageUrl, imageTitle, openImageViewer, closeImageViewer } = useImageViewer()
  const { handleResponse } = useToastMessage()
  const uploadImagesMutation = useUploadImages()
  const { mutateAsync: editStock, isPending: stockEditLoading } = useEditVehicleStock()

  const resetState = () => {
    setSlots({ chassis_image: null, engine_image: null, tire_image: null })
    setOriginalFiles({ chassis_image: null, engine_image: null, tire_image: null })
  }

  const toggle = () => {
    if (modal) resetState()
    setModal(!modal)
  }

  useEffect(() => {
    if (stock && modal) {
      const initial = {
        chassis_image: stock.chassis_image || null,
        engine_image: stock.engine_image || null,
        tire_image: stock.tire_image || null,
      }
      setSlots(initial)
      setOriginalFiles(initial)
    }
  }, [stock, modal])

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {},
    validateOnBlur: false,
    validateOnChange: false,
    onSubmit: useCallback(async () => {
      try {
        const formData = new FormData()
        const filesToUpdate = {}

        SLOTS.forEach(({ type }) => {
          const current = slots[type]
          const original = originalFiles[type]
          const isNewFile = current instanceof File
          const isDeleted = !current && original
          const isReplaced = isNewFile && original

          if (isReplaced || isDeleted) {
            formData.append("delete_file_ids[]", original.id)
          }
          if (isNewFile) {
            formData.append("images[]", current)
            filesToUpdate[type] = "new"
          } else if (isDeleted) {
            filesToUpdate[type] = null
          } else if (current?.id) {
            filesToUpdate[type] = current
          } else {
            filesToUpdate[type] = null
          }
        })

        const uploaded = { chassis_image: null, engine_image: null, tire_image: null }

        if (formData.has("images[]") || formData.has("delete_file_ids[]")) {
          // Server re-caps every uploaded confirmation photo at 100 KB.
          formData.append("max_kb", "100")
          const res = await uploadImagesMutation.mutateAsync(formData)
          if (res.success) {
            const newFiles = [...res.data]
            Object.keys(filesToUpdate).forEach(key => {
              if (filesToUpdate[key] === "new") {
                const f = newFiles.shift()
                uploaded[key] = f ? { id: f.id, url: f.url, size: f.size || 0 } : null
              } else {
                uploaded[key] = filesToUpdate[key]
              }
            })
          }
        } else {
          SLOTS.forEach(({ type }) => {
            uploaded[type] = slots[type]?.id ? slots[type] : null
          })
        }

        const stockRes = await editStock({
          id: stock?.id,
          chassis_image: uploaded.chassis_image,
          engine_image: uploaded.engine_image,
          tire_image: uploaded.tire_image,
        })

        handleResponse(stockRes, "Onboarding confirmation updated successfully.", true)
        if (stockRes.success) toggle()
      } catch (error) {
        console.error("Failed to update onboarding confirmation:", error)
        handleResponse(
          { success: false, message: error.message },
          "Failed to update. Please try again.",
          false
        )
      }
    }, [slots, originalFiles, uploadImagesMutation, editStock, stock?.id, handleResponse]),
  })

  const handleFileChange = (e, type) => {
    const file = e.target.files[0] || null
    setSlots(prev => ({ ...prev, [type]: file }))
  }
  const handleDelete = type => setSlots(prev => ({ ...prev, [type]: null }))
  const handleRestore = type => setSlots(prev => ({ ...prev, [type]: originalFiles[type] }))

  return (
    <>
      <ButtonTooltip
        type="edit"
        onClick={toggle}
        btnId="edit-onboarding-confirmation"
        btnName="Edit Confirmation Images"
      />

      <Modal isOpen={modal} toggle={toggle} centered size="lg">
        <ModalHeader toggle={toggle}>
          <div>
            <h4 className="mb-1">Onboarding Confirmation</h4>
            <p className="text-muted small mb-0 fw-normal">
              Chassis, engine and tire proof photos. Each is capped at 100 KB on upload.
            </p>
          </div>
        </ModalHeader>
        <ModalBody className="px-4 pb-4">
          <Form onSubmit={formik.handleSubmit}>
            <Row>
              {SLOTS.map(({ type, label }) => (
                <ImageSlotCard
                  key={type}
                  label={label}
                  type={type}
                  value={slots[type]}
                  originalFile={originalFiles[type]}
                  onFileChange={handleFileChange}
                  onDelete={handleDelete}
                  onRestore={handleRestore}
                  openImageViewer={openImageViewer}
                />
              ))}
            </Row>

            <div className="d-flex justify-content-end gap-2 mt-3 pt-2 border-top">
              <Button type="button" color="light" onClick={toggle} className="px-4">
                Cancel
              </Button>
              <Button
                type="submit"
                color="primary"
                disabled={stockEditLoading || uploadImagesMutation.isPending}
                className="px-5"
              >
                {stockEditLoading || uploadImagesMutation.isPending ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                    Saving...
                  </>
                ) : (
                  <>
                    <i className="bx bx-save me-2"></i>Save Changes
                  </>
                )}
              </Button>
            </div>
          </Form>
        </ModalBody>
      </Modal>

      <ImageViewer
        images={[selectedImageUrl]}
        initialIndex={0}
        isOpen={viewerOpen}
        onClose={closeImageViewer}
        title={imageTitle}
      />
    </>
  )
}
